import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ISettingDto, ConflictPolicyDto } from "../api/generated";
import { useSettings, useUpdateSettings, getLogLevelOptions } from "../hooks/useSettings";
import {
  API_BASE_URL_STORAGE_KEY,
  API_BASE_URL_DEFAULT,
  getApiBaseUrl,
} from "../api/apiClient";
import { useTranslation } from "react-i18next";

function enumEntries(enm: any): { key: string; value: any }[] {
  return Object.keys(enm)
    .filter((k) => isNaN(Number(k)))
    .map((k) => ({ key: k, value: enm[k] }));
}

function formatEnumLabel(k: string) {
  return k.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
}

const INVALID_CHARS_REGEX = /[<>:"|?*]/;
const DRIVE_PATH_REGEX = /^[a-zA-Z]:\\/;
const UNC_PATH_REGEX = /^\\\\[^\\/]+\\[^\\/]+/;

export function SettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useSettings();
  const updateMutation = useUpdateSettings();
  const [form, setForm] = React.useState<ISettingDto>();

  const [apiUrl, setApiUrl] = React.useState(() => getApiBaseUrl());
  const [apiUrlInput, setApiUrlInput] = React.useState(() => getApiBaseUrl());
  const apiUrlChanged = apiUrlInput !== apiUrl;
  const isCustomApiUrl = apiUrl !== API_BASE_URL_DEFAULT;

  function getWindowsPathError(value?: string): string | null {
    const raw = (value ?? "").trim();
    if (!raw) return t('settings.validation.required');
    const isDrive = DRIVE_PATH_REGEX.test(raw);
    const isUnc = UNC_PATH_REGEX.test(raw);
    if (!isDrive && !isUnc) return t('settings.validation.badStart');
    const toCheck = isDrive ? raw.replace(/^[a-zA-Z]:/, "") : raw;
    if (INVALID_CHARS_REGEX.test(toCheck)) return t('settings.validation.invalidChars');
    const segments = (isDrive
      ? raw.split("\\").slice(1)
      : raw.replace(/^\\\\/, "").split("\\").slice(2)
    ).filter(Boolean);
    for (const seg of segments) {
      if (seg.endsWith(".") || seg.endsWith(" ")) return t('settings.validation.badSegment');
    }
    return null;
  }

  const handleSaveApiUrl = () => {
    const trimmed = apiUrlInput.trim();
    if (!trimmed) return;
    localStorage.setItem(API_BASE_URL_STORAGE_KEY, trimmed);
    setApiUrl(trimmed);
  };

  const handleResetApiUrl = () => {
    localStorage.removeItem(API_BASE_URL_STORAGE_KEY);
    setApiUrl(API_BASE_URL_DEFAULT);
    setApiUrlInput(API_BASE_URL_DEFAULT);
  };

  React.useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const pathError = getWindowsPathError(form?.watchFolder);

  const changed = React.useMemo(() => {
    if (!data || !form) return false;
    return JSON.stringify(data) !== JSON.stringify(form);
  }, [data, form]);

  const canSave = !!form && !pathError && changed && !updateMutation.isPending;

  const handleSave = () => {
    if (!form) return;
    updateMutation.mutate(form);
  };

  if (isLoading && !form) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="sm">
        <Alert severity="error" sx={{ mt: 2 }}>
          {(error as any)?.message || t('settings.loadError')}
        </Alert>
      </Container>
    );
  }

  if (!form) return null;

  const logLevelOptions = getLogLevelOptions();
  const conflictPolicyOptions = enumEntries(ConflictPolicyDto);

  const guideItems = [
    { title: t('settings.guideWatchFolderTitle'), desc: t('settings.guideWatchFolderDesc') },
    { title: t('settings.guideLogLevelTitle'), desc: t('settings.guideLogLevelDesc') },
    { title: t('settings.guideConflictPolicyTitle'), desc: t('settings.guideConflictPolicyDesc') },
    { title: t('settings.guideApiBaseUrlTitle'), desc: t('settings.guideApiBaseUrlDesc', { default: API_BASE_URL_DEFAULT }) },
  ];

  return (
    <Box sx={{ height: '100%', bgcolor: '#F8FAFC', overflowY: 'auto' }}>
      <Container maxWidth="lg" sx={{ pt: 2 }}>
        <Stack direction="row" justifyContent="flex-end" spacing={1.5} mb={2}>
          <Button
            variant="outlined"
            disabled={!changed || updateMutation.isPending}
            onClick={() => setForm(data!)}
          >
            {t('settings.reset')}
          </Button>
          <Button
            variant="contained"
            disabled={!canSave}
            onClick={handleSave}
            sx={{ fontWeight: 700 }}
          >
            {updateMutation.isPending ? t('settings.saving') : t('settings.save')}
          </Button>
        </Stack>
        <Grid container spacing={3} columns={12}>

          {/* Columna principal */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={3}>

              {/* Configuración general */}
              <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A' }}>
                    {t('settings.general')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5, mb: 2.5 }}>
                    {t('settings.generalDesc')}
                  </Typography>
                  <Divider sx={{ mb: 3, borderColor: '#F1F5F9' }} />
                  <Stack spacing={3}>
                    <TextField
                      label={t('settings.watchFolder')}
                      value={form.watchFolder || ""}
                      onChange={(e) => setForm((p) => ({ ...p!, watchFolder: e.target.value }))}
                      error={!!pathError}
                      helperText={pathError ?? t('settings.watchFolderHelper')}
                      fullWidth
                      autoComplete="off"
                      placeholder="C:\Users\usuario\Downloads"
                    />
                    <FormControl fullWidth>
                      <InputLabel id="log-level-label">{t('settings.logLevel')}</InputLabel>
                      <Select
                        labelId="log-level-label"
                        label={t('settings.logLevel')}
                        value={form.logLevel ?? ""}
                        onChange={(e) => setForm((p) => ({ ...p!, logLevel: e.target.value as any }))}
                      >
                        {logLevelOptions.map((lvl) => (
                          <MenuItem key={lvl} value={lvl}>{lvl}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="conflict-policy-label">{t('settings.conflictPolicy')}</InputLabel>
                      <Select
                        labelId="conflict-policy-label"
                        label={t('settings.conflictPolicy')}
                        value={form.conflictPolicy ?? ""}
                        onChange={(e) =>
                          setForm((p) =>
                            p ? { ...p, conflictPolicy: e.target.value as typeof p.conflictPolicy } : p
                          )
                        }
                      >
                        {conflictPolicyOptions.map((opt) => (
                          <MenuItem key={opt.key} value={opt.value}>{formatEnumLabel(opt.key)}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </CardContent>
              </Card>

              {/* Conexión API */}
              <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A' }}>
                    {t('settings.apiConnection')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mt: 0.5, mb: 2.5 }}>
                    {t('settings.apiConnectionDesc')}
                  </Typography>
                  <Divider sx={{ mb: 3, borderColor: '#F1F5F9' }} />
                  <Stack spacing={2}>
                    <TextField
                      label={t('settings.apiBaseUrl')}
                      value={apiUrlInput}
                      onChange={(e) => setApiUrlInput(e.target.value)}
                      helperText={
                        isCustomApiUrl
                          ? t('settings.apiCustomValue', { default: API_BASE_URL_DEFAULT })
                          : process.env.REACT_APP_API_BASE_URL
                            ? t('settings.apiDefaultValueEnv')
                            : t('settings.apiDefaultValue')
                      }
                      fullWidth
                      autoComplete="off"
                    />
                    <Stack direction="row" spacing={2}>
                      <Button variant="outlined" disabled={!isCustomApiUrl} onClick={handleResetApiUrl}>
                        {t('settings.resetUrl')}
                      </Button>
                      <Button variant="contained" disabled={!apiUrlChanged || !apiUrlInput.trim()} onClick={handleSaveApiUrl}>
                        {t('settings.saveUrl')}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

            </Stack>
          </Grid>

          {/* Panel lateral */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none', position: 'sticky', top: 16 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: '#0F172A', mb: 2 }}>
                  {t('settings.guide')}
                </Typography>
                <Stack spacing={2.5}>
                  {guideItems.map(({ title, desc }) => (
                    <Box key={title}>
                      <Typography variant="body2" fontWeight={600} sx={{ color: '#1E293B', mb: 0.5 }}>{title}</Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', lineHeight: 1.65, display: 'block' }}>{desc}</Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
      <Box sx={{ pb: 6 }} />
    </Box>
  );
}
